import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

interface PerformanceMetrics {
  fcp: number | null; // First Contentful Paint
  lcp: number | null; // Largest Contentful Paint
  fid: number | null; // First Input Delay
  cls: number | null; // Cumulative Layout Shift
  ttfb: number | null; // Time to First Byte
  renderTime: number | null;
  memoryUsage: number | null;
  jsHeapSize: number | null;
}

interface PerformanceContextType {
  metrics: PerformanceMetrics;
  isLoading: boolean;
  startMeasure: (name: string) => void;
  endMeasure: (name: string) => number | null;
  logCustomMetric: (name: string, value: number) => void;
  trackUserTiming: (name: string, startTime: number) => void;
}

const PerformanceContext = createContext<PerformanceContextType | undefined>(undefined);

export const usePerformance = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
};

interface Props {
  children: React.ReactNode;
  enableAutoTracking?: boolean;
  reportingEndpoint?: string;
}

export const PerformanceProvider: React.FC<Props> = ({
  children,
  enableAutoTracking = true,
  reportingEndpoint,
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fcp: null,
    lcp: null,
    fid: null,
    cls: null,
    ttfb: null,
    renderTime: null,
    memoryUsage: null,
    jsHeapSize: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const measurementsRef = useRef<Map<string, number>>(new Map());

  // Web Vitals tracking
  useEffect(() => {
    if (!enableAutoTracking) return;

    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();

      entries.forEach(entry => {
        const updatedMetrics = { ...metrics };

        switch (entry.entryType) {
          case 'navigation': {
            const navEntry = entry as PerformanceNavigationTiming;
            updatedMetrics.ttfb = navEntry.responseStart - navEntry.requestStart;
            break;
          }

          case 'paint':
            if (entry.name === 'first-contentful-paint') {
              updatedMetrics.fcp = entry.startTime;
            }
            break;

          case 'largest-contentful-paint':
            updatedMetrics.lcp = entry.startTime;
            break;

          case 'first-input': {
            const fidEntry = entry as PerformanceEventTiming;
            updatedMetrics.fid = fidEntry.processingStart - fidEntry.startTime;
            break;
          }

          case 'layout-shift':
            if (!(entry as any).hadRecentInput) {
              updatedMetrics.cls = (updatedMetrics.cls || 0) + (entry as any).value;
            }
            break;
        }

        setMetrics(updatedMetrics);
      });
    });

    // Observe different entry types
    try {
      observer.observe({
        entryTypes: [
          'navigation',
          'paint',
          'largest-contentful-paint',
          'first-input',
          'layout-shift',
        ],
      });
    } catch (error) {
      console.warn('Performance Observer not fully supported:', error);
    }

    return () => observer.disconnect();
  }, [enableAutoTracking]);

  // Memory usage tracking
  useEffect(() => {
    const trackMemoryUsage = () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        setMetrics(prev => ({
          ...prev,
          memoryUsage: memory.usedJSHeapSize,
          jsHeapSize: memory.totalJSHeapSize,
        }));
      }
    };

    const interval = setInterval(trackMemoryUsage, 5000); // Check every 5 seconds
    trackMemoryUsage(); // Initial check

    return () => clearInterval(interval);
  }, []);

  // Component render time tracking
  useEffect(() => {
    const renderStart = performance.now();

    const timeout = setTimeout(() => {
      const renderTime = performance.now() - renderStart;
      setMetrics(prev => ({
        ...prev,
        renderTime,
      }));
      setIsLoading(false);
    }, 0);

    return () => clearTimeout(timeout);
  }, []);

  // Report metrics to endpoint
  useEffect(() => {
    if (reportingEndpoint && !isLoading) {
      const reportMetrics = async () => {
        try {
          await fetch(reportingEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              metrics,
              timestamp: Date.now(),
              userAgent: navigator.userAgent,
              url: window.location.href,
            }),
          });
        } catch (error) {
          console.warn('Failed to report performance metrics:', error);
        }
      };

      // Report metrics after a delay to ensure they're populated
      const timeout = setTimeout(reportMetrics, 3000);
      return () => clearTimeout(timeout);
    }
  }, [metrics, isLoading, reportingEndpoint]);

  const startMeasure = (name: string) => {
    measurementsRef.current.set(name, performance.now());
  };

  const endMeasure = (name: string): number | null => {
    const startTime = measurementsRef.current.get(name);
    if (startTime) {
      const duration = performance.now() - startTime;
      measurementsRef.current.delete(name);

      // Log to Performance Timeline
      if (performance.mark && performance.measure) {
        try {
          performance.mark(`${name}-end`);
          performance.measure(name, { start: startTime, end: performance.now() });
        } catch (error) {
          console.warn('Performance measurement failed:', error);
        }
      }

      return duration;
    }
    return null;
  };

  const logCustomMetric = (name: string, value: number) => {
    // Log custom metric to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`Performance Metric [${name}]:`, `${value.toFixed(2)}ms`);
    }

    // Send to analytics if available
    if ((window as any).gtag) {
      (window as any).gtag('event', 'timing_complete', {
        name,
        value: Math.round(value),
      });
    }
  };

  const trackUserTiming = (name: string, startTime: number) => {
    const duration = performance.now() - startTime;
    logCustomMetric(name, duration);
  };

  const contextValue: PerformanceContextType = {
    metrics,
    isLoading,
    startMeasure,
    endMeasure,
    logCustomMetric,
    trackUserTiming,
  };

  return <PerformanceContext.Provider value={contextValue}>{children}</PerformanceContext.Provider>;
};

// Higher-order component for automatic component performance tracking
export const withPerformanceTracking = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName?: string
) => {
  const PerformanceTrackedComponent = (props: P) => {
    const { startMeasure, endMeasure, logCustomMetric } = usePerformance();
    const trackedName =
      componentName || WrappedComponent.displayName || WrappedComponent.name || 'Component';

    useEffect(() => {
      startMeasure(`${trackedName}-render`);
      return () => {
        const duration = endMeasure(`${trackedName}-render`);
        if (duration !== null) {
          logCustomMetric(`${trackedName}-render-time`, duration);
        }
      };
    }, []);

    return <WrappedComponent {...props} />;
  };

  PerformanceTrackedComponent.displayName = `withPerformanceTracking(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return PerformanceTrackedComponent;
};

// Hook for component-level performance tracking
export const useComponentPerformance = (componentName: string) => {
  const { startMeasure, endMeasure, logCustomMetric } = usePerformance();

  useEffect(() => {
    const renderStart = performance.now();
    startMeasure(`${componentName}-mount`);

    return () => {
      const mountDuration = endMeasure(`${componentName}-mount`);
      const renderDuration = performance.now() - renderStart;

      if (mountDuration !== null) {
        logCustomMetric(`${componentName}-mount-time`, mountDuration);
      }
      logCustomMetric(`${componentName}-render-time`, renderDuration);
    };
  }, [componentName, startMeasure, endMeasure, logCustomMetric]);
};

export default PerformanceProvider;
